import React, { useId, useState } from 'react';
import { Loader2 } from 'lucide-react';
import scaleIcon from 'src/assets/images/plans/scale.webp';
import proIcon from 'src/assets/images/plans/pro.webp';
import inHouseIcon from 'src/assets/images/plans/in-house-team.webp';
import {
  DEFAULT_PLAN_INTERVAL,
  PLAN_INTERVALS,
  type PlanInterval,
  planCardPrices,
} from 'src/features/expired-window/expired-window.logic';
import type { StripePlan } from 'src/lib/services/api/subscription-api/subscription-client.types';

// The website's card copy (becreatives.co/pricing), and the plan icons Toma sent (29 Oct)
const PLAN_COPY: Record<
  string,
  { name: string; tagline: string; icon: string; features: string[] }
> = {
  scale: {
    name: 'Scale Plan',
    tagline: 'Perfect for businesses with steady content and zero stress',
    icon: scaleIcon,
    features: [
      '1 business day turnaround*',
      'Unlimited revisions',
      'Dedicated editing team + account manager',
      'Quality check on every video',
      'Slack channel with your team',
      'Up to 2 brands & 2 users',
    ],
  },
  pro: {
    name: 'Pro Plan',
    tagline: 'Best for when high-volume, fast-turnaround is your jam',
    icon: proIcon,
    features: [
      'Multiple videos per day*',
      'Unlimited revisions',
      'Dedicated editing team + account manager',
      'Quality check on every video',
      'Slack channel with your team',
      'Unlimited brands & users',
    ],
  },
  in_house_team: {
    name: 'In House Team',
    tagline: 'Your own creative department, just without the hiring headaches.',
    icon: inHouseIcon,
    features: [
      'All in PRO Plan +',
      'Custom Turnaround',
      'Dedicated Video Editor',
      'Simultaneous requests',
    ],
  },
};
// Pro is the plan most clients pick; it isn't the biggest saving, so not "Best deal" (Birute)
const MOST_POPULAR_SLUG = 'pro';

const FeatureTick: React.FC = () => (
  <svg
    width={17}
    height={17}
    viewBox={'0 0 24 24'}
    fill={'none'}
    aria-hidden
    className={'tw-mt-px tw-shrink-0'}
  >
    <circle cx={12} cy={12} r={10} fill={'currentColor'} />
    <path
      d={'M8 12.2l2.7 2.7L16 9.6'}
      stroke={'#fff'}
      strokeWidth={2.1}
      strokeLinecap={'round'}
      strokeLinejoin={'round'}
    />
  </svg>
);

const Pill: React.FC<{ children: React.ReactNode; tone?: 'plain' | 'accent' }> = ({
  children,
  tone = 'plain',
}) => (
  <span
    className={`tw-rounded-full tw-border tw-px-2.5 tw-py-1 tw-text-[11.5px] tw-font-bold ${
      tone === 'accent'
        ? 'tw-border-expired-accent tw-bg-expired-accent tw-text-white'
        : 'tw-border-[#E7E6EE] tw-bg-white tw-text-expired-ink'
    }`}
  >
    {children}
  </span>
);

/**
 * The plan cards (Toma's design, 29 Oct): one Monthly / Quarterly / Yearly switch over Scale, Pro
 * and In House Team, side by side on desktop and stacked on mobile, per-month prices as on the
 * website. Never Ben, never Platform Plan. Shared by the Expired window's Change plan and the
 * sign-up plan page; each decides what choosing a plan does.
 */
const PlanCards: React.FC<{
  plans: StripePlan[] | undefined;
  // The client's own plan, marked "Your plan"; its note shows on its own billing interval only
  current?: { slug: string; interval: string | null; note: string | null } | null;
  // The plan being opened, with its spinner; every Select is disabled meanwhile
  pendingPriceId: string | null;
  // Every Select disabled for something else on the page (the Expired window's Reactivate)
  disabled?: boolean;
  onChoose: (priceId: string) => void;
  // Shown right under the cards, where the client just tapped
  error?: React.ReactNode;
  // False where the page already titles it "Pick your plan", so it isn't announced twice
  showHeading?: boolean;
}> = ({
  plans,
  current = null,
  pendingPriceId,
  disabled = false,
  onChoose,
  error,
  showHeading = true,
}) => {
  const headingId = useId();
  const [interval, setBillingInterval] = useState<PlanInterval>(DEFAULT_PLAN_INTERVAL);
  // A plan the cards have no copy for is left out rather than breaking the page
  const cards = planCardPrices(plans, interval).filter((card) => PLAN_COPY[card.slug]);

  return (
    <section
      {...(showHeading ? { 'aria-labelledby': headingId } : { 'aria-label': 'Pick your plan' })}
      className={'tw-font-jakarta tw-text-expired-ink'}
    >
      {showHeading && (
        <h2 id={headingId} className={'tw-mb-4 tw-text-xl tw-font-extrabold'}>
          Pick your plan
        </h2>
      )}

      <div
        role={'group'}
        aria-label={'Billing'}
        className={
          'tw-mb-6 tw-inline-flex tw-flex-wrap tw-items-center tw-rounded-xl tw-border tw-border-[#E7E6EE] tw-bg-[#F6F6F9] tw-p-1'
        }
      >
        {PLAN_INTERVALS.map((option) => (
          <button
            key={option.id}
            type={'button'}
            aria-pressed={interval === option.id}
            onClick={() => setBillingInterval(option.id)}
            className={`tw-h-[38px] tw-rounded-[9px] tw-px-5 tw-text-sm tw-font-bold ${
              interval === option.id ? 'tw-bg-expired-ink tw-text-white' : 'tw-text-expired-body'
            }`}
          >
            {option.label}
          </button>
        ))}
      </div>

      {!cards.length && (
        <p className={'tw-mb-4 tw-text-[15px] tw-text-expired-body'}>
          No plan is sold{' '}
          {PLAN_INTERVALS.find((option) => option.id === interval)?.label.toLowerCase()} right now.
          Choose another billing option.
        </p>
      )}

      <ul className={'tw-grid tw-grid-cols-1 tw-items-start tw-gap-4 lg:tw-grid-cols-3'}>
        {cards.map((card) => {
          const copy = PLAN_COPY[card.slug];
          const isPopular = card.slug === MOST_POPULAR_SLUG;
          const isCurrent = current?.slug === card.slug;

          return (
            <li
              key={card.slug}
              className={`tw-relative tw-rounded-[18px] tw-border tw-px-5 tw-pb-6 tw-pt-[22px] ${
                isPopular
                  ? 'tw-border-[#E0DFE7] tw-bg-[#F5F5F7]'
                  : 'tw-border-[#E7E6EE] tw-bg-white'
              } ${isCurrent ? 'tw-ring-2 tw-ring-expired-accent' : ''}`}
            >
              {(isPopular || isCurrent) && (
                <div className={'tw-absolute tw-right-4 tw-top-4 tw-flex tw-gap-1.5'}>
                  {isCurrent && <Pill tone={'accent'}>Your plan</Pill>}
                  {isPopular && <Pill>Most popular</Pill>}
                </div>
              )}
              <img
                src={copy.icon}
                alt={''}
                width={64}
                height={64}
                className={'tw-mb-4 tw-mt-1.5 tw-size-16'}
              />
              <h3 className={'tw-text-[19px] tw-font-extrabold tw-tracking-[-0.3px]'}>
                {copy.name}
              </h3>
              <p
                className={
                  'tw-mt-2 tw-text-[13.5px] tw-leading-normal tw-text-[#7A7886] lg:tw-min-h-10'
                }
              >
                {copy.tagline}
              </p>

              <p className={'tw-mb-0.5 tw-mt-4 tw-flex tw-items-end tw-gap-[7px]'}>
                <span
                  className={
                    'tw-text-[30px] tw-font-extrabold tw-leading-none tw-tracking-[-1.1px]'
                  }
                >
                  {card.perMonth}
                </span>
                <span
                  className={
                    'tw-pb-0.5 tw-text-[11.5px] tw-font-semibold tw-leading-tight tw-text-expired-quiet'
                  }
                >
                  per
                  <br />
                  month
                </span>
              </p>
              <p className={'tw-min-h-[18px] tw-text-[12.5px] tw-text-expired-quiet'}>
                {card.billed}
              </p>
              <p className={'tw-mt-1.5 tw-min-h-5'}>
                {card.saving && (
                  <span
                    className={
                      'tw-inline-block tw-rounded-full tw-bg-[#E7F7F1] tw-px-[9px] tw-py-[3px] tw-text-[11.5px] tw-font-extrabold tw-text-[#0B8A5F]'
                    }
                  >
                    {card.saving}
                  </span>
                )}
              </p>
              {isCurrent && current?.note && current.interval === interval && (
                <p className={'tw-mt-2 tw-text-[12.5px] tw-font-semibold tw-text-expired-meta'}>
                  {current.note}
                </p>
              )}

              {/* Every plan, In House Team too, as Stripe's pricing table also let it be bought
                straight away; "Book a call" stays on the website only (Toma, 29 Oct) */}
              <button
                type={'button'}
                disabled={disabled || pendingPriceId !== null}
                aria-busy={pendingPriceId === card.priceId}
                onClick={() => onChoose(card.priceId)}
                className={`tw-mt-3.5 tw-flex tw-h-[46px] tw-w-full tw-items-center tw-justify-center tw-gap-2 tw-rounded-[11px] tw-text-[15px] tw-font-bold tw-text-white hover:tw-opacity-90 disabled:tw-opacity-70 ${
                  isPopular ? 'tw-bg-expired-accent' : 'tw-bg-expired-ink'
                }`}
              >
                {pendingPriceId === card.priceId && (
                  <Loader2 aria-label={'Opening payment'} className={'tw-size-4 tw-animate-spin'} />
                )}
                Select plan
              </button>

              <p className={'tw-mb-2.5 tw-mt-[18px] tw-text-[13.5px] tw-text-expired-meta'}>
                This includes:
              </p>
              <ul className={'tw-flex tw-flex-col tw-gap-[9px]'}>
                {copy.features.map((feature) => (
                  <li
                    key={feature}
                    className={
                      'tw-flex tw-items-start tw-gap-[9px] tw-text-[13.5px] tw-leading-snug'
                    }
                  >
                    <FeatureTick />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
            </li>
          );
        })}
      </ul>

      {/* Next to the cards, where the client just tapped, not off screen above them */}
      {error && <div className={'tw-mt-4'}>{error}</div>}

      <div
        className={'tw-mt-5 tw-flex tw-flex-col tw-gap-1 tw-text-[12.5px] tw-text-expired-quiet'}
      >
        <p>
          * Turnaround times are based on average request complexity. Actual times may vary with the
          specifics of your request.
        </p>
        <p>Prices are in US dollars. Checkout may show the amount in your local currency.</p>
      </div>
    </section>
  );
};

export { PlanCards };
